/* eslint-disable */
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Box, Tooltip } from "@mui/material";
import type { NodeKey } from "lexical";
import { useCallback, useState } from "react";
import { useLexicalContext } from "../lexical-context";
import { RESIZE_END_COMMAND, RESIZE_START_COMMAND } from "../types/commands";
import { useSelectable } from "../utils/use-selectable";
import { type Compass, Ordinals, useImageResizer } from "./hooks/use-image-resizer";

export const HandleSize = 16;

type Props = {
  proportionOfMaxWidth: number;
  aspect: number;
  children: React.JSX.Element;
  disabled: boolean;
  title: string;
  maxWidth?: number;
  maxHeight?: number;
  nodeKey: NodeKey;
  resizable: boolean;
};

export function ImageTools({
  proportionOfMaxWidth,
  aspect,
  children,
  disabled,
  resizable,
  title,
  maxWidth,
  maxHeight = 10000,
  nodeKey,
}: Props): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const {
    focus: { focused },
  } = useLexicalContext();
  const selected = useSelectable(nodeKey);
  const [hover, setHover] = useState(false);

  const onResizeEnd = useCallback(
    (newProportionOfMaxWidth: number): void => {
      editor.dispatchCommand(RESIZE_END_COMMAND, {
        nodeKey,
        proportionOfMaxWidth: newProportionOfMaxWidth,
      });
    },
    [editor, nodeKey],
  );

  const onResizeStart = useCallback((): void => {
    editor.dispatchCommand(RESIZE_START_COMMAND, {
      nodeKey,
    });
  }, [editor, nodeKey]);

  const { controlWrapperRef, positioningRef, handlePointerDown } = useImageResizer({
    onResizeStart,
    onResizeEnd,
    proportionOfMaxWidth,
    maxWidth,
    maxHeight,
  });

  return (
    <Tooltip followCursor title={title}>
      <Box
        ref={controlWrapperRef}
        // These can't undo if in sx
        style={{
          width: `${proportionOfMaxWidth * 100}%`,
          aspectRatio: aspect,
        }}
        sx={{
          position: "relative",
          boxSizing: "content-box",
          display: "block",
          ...(disabled
            ? {}
            : {
                zIndex: 1,
                margin: `-4px`,
                padding: `4px`,
              }),
          ...(!disabled && !resizable && selected && focused
            ? { backgroundColor: "background.selected" }
            : {}),
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            ...(disabled
              ? {}
              : {
                  border: "2px solid",
                  borderColor: (focused && selected) || hover ? "info.main" : "transparent",
                  pointerEvents: "none",
                  userSelect: "none",
                }),
          }}
        >
          {children}
        </Box>
        {!disabled &&
          resizable &&
          (hover || (selected && focused) || positioningRef.current.isResizing) &&
          ["ne", "se", "sw", "nw"].map((c) => (
            <Box
              key={c}
              sx={{
                position: "absolute",
                width: `${HandleSize * 2}px`,
                height: `${HandleSize * 2}px`,
                margin: "auto",
                top: "0",
                left: "0",
                bottom: "0",
                right: "0",
                cursor: `${c}-resize`,
                ...{ [Ordinals[c[0]!]!]: undefined },
                ...{ [Ordinals[c[1]!]!]: undefined },
              }}
              onPointerDown={(event) => handlePointerDown(event, c as Compass)}
              data-testid={`image-resize-handle-${c}`}
            >
              <Box
                sx={(theme) => ({
                  position: "absolute",
                  background:
                    positioningRef.current.isResizing && positioningRef.current.direction === c
                      ? theme.palette.primary.light
                      : theme.palette.neutral[100],
                  border: `2px solid ${theme.palette.info.main}`,
                  width: `${HandleSize}px`,
                  height: `${HandleSize}px`,
                  margin: "auto",
                  top: "0",
                  left: "0",
                  bottom: "0",
                  right: "0",
                  ...{ [Ordinals[c[0]!]!]: undefined },
                  ...{ [Ordinals[c[1]!]!]: undefined },
                })}
              ></Box>
            </Box>
          ))}
      </Box>
    </Tooltip>
  );
}
